import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useC516, ItemState, QueryFilters } from './hooks/useC516';
import { c516Api } from '../../services/c516Api';
import { BatchAcceptanceBridge, pwc516f1AcceptanceManifest } from '../../features/acceptance';
import './C516Form.css';

// -- Helpers --
const fmtNumber = (v: number | null | undefined): string => {
  if (v == null) return '';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const fmtPercent = (v: number | null | undefined): string => {
  if (v == null) return '';
  return v.toFixed(2) + '%';
};

// -- Component --
const C516Form: React.FC = () => {
  const navigate = useNavigate();
  const [scStart, setScStart] = useState('');
  const [scEnd, setScEnd] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [salesStart, setSalesStart] = useState('');
  const [salesEnd, setSalesEnd] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
    lastApiResponse,
  } = useC516();

  // -- Query --
  const onQuery = useCallback(() => {
    const filters: QueryFilters = {
      scStart, scEnd, dateStart, dateEnd, salesStart, salesEnd,
    };
    handleQuery(filters);
  }, [handleQuery, scStart, scEnd, dateStart, dateEnd, salesStart, salesEnd]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // -- Execute --
  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認結案',
      content: `確定要結案已勾選的 ${selectedCount} 筆S/C？`,
      okText: '確定結案',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  // -- Select All --
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // -- Table columns --
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) =>
            handleToggleItem(record.item.fc110_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: 'S/C 單號',
      dataIndex: ['item', 'fc110_01'],
      key: 'fc110_01',
      width: 120,
    },
    {
      title: 'S/C 日期',
      dataIndex: ['item', 'fc110_11'],
      key: 'fc110_11',
      width: 120,
    },
    {
      title: '客戶名稱',
      dataIndex: ['item', 'fc110_12'],
      key: 'fc110_12',
      width: 200,
      ellipsis: true,
    },
    {
      title: '業績客戶',
      dataIndex: ['item', 'fc110_59'],
      key: 'fc110_59',
      width: 120,
      ellipsis: true,
    },
    {
      title: '預估毛利',
      dataIndex: ['item', 'fc110_99'],
      key: 'fc110_99',
      width: 130,
      align: 'right',
      render: (v: number) => fmtNumber(v),
    },
    {
      title: '毛利率',
      dataIndex: ['item', 'fc110_100'],
      key: 'fc110_100',
      width: 100,
      align: 'right',
      render: (v: number) => fmtPercent(v),
    },
  ];

  // -- Render --
  return (
    <div className="c516-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '結案執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => { e.preventDefault(); navigate('/erp'); }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => { e.preventDefault(); navigate('/erp'); }}
              >
                銷貨管理
              </a>
              <span>/</span>
              <span className="current">S/C 結案核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>S/C 結案核准</h1>
            <span className="badge">C516</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label>S/C 單號:</label>
          <Input
            value={scStart}
            onChange={(e) => setScStart(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始"
            style={{ width: 130 }}
            allowClear
          />
          <span className="separator">~</span>
          <Input
            value={scEnd}
            onChange={(e) => setScEnd(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束"
            style={{ width: 130 }}
            allowClear
          />

          <label data-contract-field="fc110_11">日期:</label>
          <DatePicker
            value={dateStart ? dayjs(dateStart) : null}
            onChange={(d) => setDateStart(d ? d.format('YYYY-MM-DD') : '')}
            placeholder="起始"
            style={{ width: 130 }}
            allowClear
          />
          <span className="separator">~</span>
          <DatePicker
            value={dateEnd ? dayjs(dateEnd) : null}
            onChange={(d) => setDateEnd(d ? d.format('YYYY-MM-DD') : '')}
            placeholder="結束"
            style={{ width: 130 }}
            allowClear
          />

          <label data-contract-field="fc110_13">業務員:</label>
          <Input
            value={salesStart}
            onChange={(e) => setSalesStart(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始"
            style={{ width: 110 }}
            allowClear
          />
          <span className="separator">~</span>
          <Input
            value={salesEnd}
            onChange={(e) => setSalesEnd(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束"
            style={{ width: 110 }}
            allowClear
          />

          <Button type="primary" onClick={onQuery} loading={loading} data-acceptance-action="PWC516-ACT-QUERY">
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fc110_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入篩選條件並點擊「確定範圍」查詢待結案的S/C
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
            data-acceptance-action="PWC516-ACT-CLOSEOUT"
          >
            結案 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwc516f1AcceptanceManifest}
          records={items}
          selectedKeys={items.filter((item) => item.selected).map((item) => item.item.fc110_01)}
          keyOf={(item) => item.item.fc110_01}
          queryExact={(key) => c516Api.query({ sc_start: key, sc_end: key })}
          lastApiResponse={lastApiResponse}
          loading={loading || executeLoading}
          onAutoFill={() => {
            const first = items[0];
            if (first) handleToggleItem(first.item.fc110_01, true);
          }}
          autoFillDisabled={items.length === 0}
          autoFillReason="自動填表只勾選第一筆可結案 S/C，不會自動執行結案。"
          screenDraft={{ sc_start: scStart, sc_end: scEnd, date_start: dateStart, date_end: dateEnd, selected_items: items.filter((item) => item.selected) }}
        />
      </div>
    </div>
  );
};

export default C516Form;
